'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { YMMData } from '@/interfaces/site/SiteYMMData';
import { getCapabilities, getMakesForYear, getModelsForMake, getYearsFrom } from '@/packages/sxm/ymm';
import type { Model } from '@/packages/sxm/ymm/types';

const EMPTY_YMM: YMMData = {
    year: '',
    make: '',
    model: '',
    id: '',
};

export interface YmmOptions {
    years: string[];
    makes: string[];
    models: Model[];
}

/**
 * `YMMData` marks every field optional because callers receive partially-filled vehicles (a cookie
 * with only a year, a device lookup with only a radio ID). This hook is stricter than that: it seeds
 * each field to `''` and every writer below keeps them strings, so an unselected value is `''` rather
 * than `undefined`. Modelling that here is what lets `selection.year` be passed straight to loaders
 * that require a `string`. `YmmSelection` is still assignable to `YMMData`, so the returned
 * `ymmData` continues to satisfy consumers like `onCarReady`.
 */
type YmmSelection = Required<Pick<YMMData, 'year' | 'make' | 'model' | 'id'>>;

export interface UseYmmSelectorOptions {
    initialSelection?: YMMData;
    startYear?: string;
}

export function useYmmSelector({ initialSelection = EMPTY_YMM, startYear = '2004' }: UseYmmSelectorOptions = {}) {
    const [ymmOptions, setYmmOptions] = useState<YmmOptions>({
        years: [],
        makes: [],
        models: [],
    });
    const [selection, setSelectionState] = useState<YmmSelection>(() => ({
        year: initialSelection.year || '',
        make: initialSelection.make || '',
        model: initialSelection.model || '',
        id: initialSelection.id || '',
    }));
    const [capabilities, setCapabilities] = useState(initialSelection.capabilities);
    const yearsRequest = useRef<Promise<string[]> | null>(null);
    const makesRequestId = useRef(0);
    const modelsRequestId = useRef(0);
    const capabilitiesRequestId = useRef(0);

    const loadYears = useCallback(async () => {
        if (ymmOptions.years.length) {
            return ymmOptions.years;
        }

        if (!yearsRequest.current) {
            yearsRequest.current = getYearsFrom(startYear).finally(() => {
                yearsRequest.current = null;
            });
        }

        const years = await yearsRequest.current;
        setYmmOptions((current) => ({ ...current, years }));
        return years;
    }, [startYear, ymmOptions.years]);

    const loadMakes = useCallback(async (year: string) => {
        const requestId = ++makesRequestId.current;
        const makes = await getMakesForYear(year);

        if (requestId === makesRequestId.current) {
            setYmmOptions((current) => ({ ...current, makes }));
        }

        return makes;
    }, []);

    const loadModels = useCallback(async (year: string, make: string) => {
        const requestId = ++modelsRequestId.current;
        const models = await getModelsForMake(year, make);

        if (requestId === modelsRequestId.current) {
            setYmmOptions((current) => ({ ...current, models }));
        }

        return models;
    }, []);

    const loadSelectionOptions = useCallback(
        async (year: string, make: string) => {
            const requests: Array<Promise<unknown>> = [];

            if (year) {
                requests.push(loadMakes(year));
            }
            if (year && make) {
                requests.push(loadModels(year, make));
            }

            await Promise.all(requests);
        },
        [loadMakes, loadModels],
    );

    const setSelection = useCallback((nextSelection: YMMData) => {
        capabilitiesRequestId.current += 1;
        setCapabilities(nextSelection.capabilities);
        setSelectionState({
            year: nextSelection.year || '',
            make: nextSelection.make || '',
            model: nextSelection.model || '',
            id: nextSelection.id || '',
        });
    }, []);

    const selectYear = useCallback(
        (year: string) => {
            if (!year || selection.year === year) {
                return;
            }

            makesRequestId.current += 1;
            modelsRequestId.current += 1;
            capabilitiesRequestId.current += 1;
            setCapabilities(undefined);
            setSelectionState({ year, make: '', model: '', id: '' });
            setYmmOptions((current) => ({ ...current, makes: [], models: [] }));
            void loadMakes(year);
        },
        [loadMakes, selection.year],
    );

    const selectMake = useCallback(
        (make: string) => {
            if (!make || selection.make === make) {
                return;
            }

            modelsRequestId.current += 1;
            capabilitiesRequestId.current += 1;
            setCapabilities(undefined);
            setSelectionState((current) => ({ ...current, make, model: '', id: '' }));
            setYmmOptions((current) => ({ ...current, models: [] }));
            void loadModels(selection.year, make);
        },
        [loadModels, selection.make, selection.year],
    );

    const selectModel = useCallback(
        (model: Model) => {
            if (!model.vehicleModel || selection.model === model.vehicleModel) {
                return;
            }

            capabilitiesRequestId.current += 1;
            setCapabilities(undefined);
            setSelectionState((current) => ({
                ...current,
                model: model.vehicleModel,
                id: model.ymmComboId,
            }));
        },
        [selection.model],
    );

    useEffect(() => {
        const id = selection.id;
        if (!id) {
            return;
        }

        const requestId = ++capabilitiesRequestId.current;
        void getCapabilities(id).then((nextCapabilities) => {
            if (requestId === capabilitiesRequestId.current) {
                setCapabilities(nextCapabilities);
            }
        });
    }, [selection.id]);

    const ymmData = useMemo(() => ({ ...selection, capabilities }), [capabilities, selection]);

    return {
        ymmData,
        ymmOptions,
        loadYears,
        loadSelectionOptions,
        setSelection,
        selectYear,
        selectMake,
        selectModel,
    };
}
